/**
 * Application state, and the little of it that outlives a visit.
 *
 * There is no account: favourites live in this browser's localStorage and
 * nowhere else. Reads and writes are wrapped because private-mode Safari
 * throws on both.
 */

import { createFilterState } from './filters.js';
import { fold } from './format.js';

const KEYS = {
  fav: 'vem.fav',
  recenti: 'vem.recenti',
};

/** How many past searches are worth offering before the list becomes a chore. */
const MAX_RECENTI = 5;

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable or full: the session still works, it just forgets.
  }
}

export function createAppState() {
  const fav = read(KEYS.fav, []);
  const recenti = read(KEYS.recenti, []);

  return {
    filtri: createFilterState(),
    /** Free-text search, applied on top of the filters. */
    q: '',
    fav: new Set(Array.isArray(fav) ? fav : []),
    /**
     * Past searches, newest first. Kept because the search overlay throws the
     * query away every time it closes, which is right — leaving the catalogue
     * narrowed by words nothing on screen still shows would be a trap — but it
     * also means a visitor who wants the same thing twice types it twice.
     */
    recenti: (Array.isArray(recenti) ? recenti : []).filter(
      (entry) => typeof entry === 'string'
    ),
    /** Visitor position, or null while unknown. */
    me: null,
    /**
     * Whether that position came from the device or is the fallback centre of
     * Rome. A distance from a guessed origin is not a distance, so it decides
     * whether one is shown at all.
     */
    meExact: false,
    /** Id of the venue selected on the map. */
    attivo: null,
    /** Tab to come back to when leaving the programme screen. */
    tab: 's-mappa',
  };
}

export function toggleFavourite(state, id) {
  if (state.fav.has(id)) state.fav.delete(id);
  else state.fav.add(id);
  write(KEYS.fav, [...state.fav]);
}

/**
 * Remembers a search that worked.
 *
 * Called when a venue is opened from the results, not on every keystroke: the
 * prefixes somebody types on the way to a word are not searches they ever
 * meant, and a list of "t", "tr", "tra" is worse than no list. Opening a
 * result is the moment the search is known to have found something.
 *
 * Compared folded, so "citta" typed today does not sit in the list next to
 * "città" typed yesterday as though they were different searches.
 */
export function rememberSearch(state, query) {
  const kept = String(query ?? '').trim();
  if (kept.length < 2) return;

  const same = fold(kept);
  state.recenti = [kept, ...state.recenti.filter((entry) => fold(entry) !== same)].slice(
    0,
    MAX_RECENTI
  );
  write(KEYS.recenti, state.recenti);
}

export function forgetSearches(state) {
  state.recenti = [];
  write(KEYS.recenti, state.recenti);
}
